import { useQuery } from '@tanstack/react-query'
import { makeApiRequest } from 'apis/birdeye/helpers'
import { milliseconds } from 'date-fns'
import store from 'stores/store'

export interface TokenCreationInfo {
  txHash: string | null
  slot: number | null
  tokenAddress: string | null
  decimals: number | null
  owner: string | null
  blockUnixTime: number | null
  blockHumanTime: string | null
}

const fetchTokenGenesis = async (
  address: string | undefined,
): Promise<TokenCreationInfo | null> => {
  if (!address) return null
  try {
    const searchParams = new URLSearchParams({ address })
    const query = `defi/token_creation_info?${searchParams.toString()}`
    const data = await makeApiRequest<TokenCreationInfo>(query)
    if (data?.success && data?.data) {
      const slot = data.data?.slot
      if (slot && data.data.blockUnixTime == null) {
        const connection = store.getState().connection
        data.data.blockUnixTime = await connection.getBlockTime(slot)
      }
      return data.data
    } else return null
  } catch (e) {
    console.error('failed to fetch birdeye token genesis', e)
    return null
  }
}

export interface TokenBalance {
  address: string | null
  decimals: number | null
  balance: number | null
  uiAmount: number | null
  chainId: string | null
  logoURI: string | null
  name: string | null
  symbol: string | null
  priceUsd: number | null
  valueUsd: number | null
}

const fetchCreatorBalance = async (
  wallet: string | undefined,
  address: string | undefined,
): Promise<TokenBalance | null> => {
  if (!wallet || !address) return null
  try {
    const searchParams = new URLSearchParams({ wallet, token_address: address })
    const query = `v1/wallet/token_balance?${searchParams.toString()}`
    const { data } = await makeApiRequest<TokenBalance>(query)
    return data ?? null
  } catch (e) {
    console.error('failed to fetch birdeye creator wallet balance', e)
    return null
  }
}

export function useTokenGenesis() {
  const mint = store((s) => s.market.baseToken.mint)
  // const { asPath } = useRouter()

  // // get the base token from the url
  // const baseAddress = useMemo(() => {
  //   const address = asPath.split('/').pop() as string
  //   return address
  // }, [asPath])

  const { data: creationData } = useQuery({
    queryKey: ['token-genesis', mint],
    queryFn: () => fetchTokenGenesis(mint),
    cacheTime: milliseconds({ minutes: 15 }),
    staleTime: milliseconds({ minutes: 10 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!mint,
  })

  const { data: creatorBalance } = useQuery({
    queryKey: ['creator-token-balance', mint],
    queryFn: () => {
      if (!creationData?.owner) {
        throw new Error('creationData.owner is nullish')
      }
      return fetchCreatorBalance(creationData.owner, mint)
    },
    cacheTime: milliseconds({ minutes: 15 }),
    staleTime: milliseconds({ minutes: 10 }),
    retry: 3,
    refetchOnWindowFocus: false,
    enabled: !!creationData?.owner,
  })

  return { creationData, creatorBalance: creatorBalance?.uiAmount ?? 0 }
}
